import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { get, ref, push, set, remove } from 'firebase/database';
import { db } from '../common/firebase';

// Formats a poem object with default values and proper data types
const formatPoemData = (poem, id) => ({
    id,
    ...poem,
    createdAt: Number(poem.createdAt) || Date.now(),
    lastUpdated: Number(poem.lastUpdated) || Date.now(),
    bookmarkedData: poem.bookmarkedBy || {},
    likes: poem.likes || {},
    comments: poem.comments || {}
});

// Selectors
export const selectAllPoems = state => state.poems.poems;
export const selectPoemsLoading = state => state.poems.loading;
export const selectPoemsError = state => state.poems.error;

// NEW: Selectors for bookmarked poems
export const selectBookmarkedPoems = state => state.poems.bookmarkedPoems;
export const selectBookmarkedPoemsCount = state => state.poems.totalBookmarkedCount;

// Async thunk to fetch poems with pagination, search, and type filtering
export const fetchPoems = createAsyncThunk(
    'poems/fetchPoems',
    async ({ page, pageSize, searchTerm, selectedType }, { rejectWithValue }) => {
        try {
            // Fetch all poems from Firebase
            const poemsRef = ref(db, 'poems');
            const snapshot = await get(poemsRef);
            const poems = snapshot.val() || {};

            // Convert to array and format data
            let poemsArray = Object.keys(poems)
                .map(key => formatPoemData(poems[key], key));

            // Sort by creation date (newest first)
            poemsArray.sort((a, b) => b.createdAt - a.createdAt);

            // Apply search filter if provided
            if (searchTerm) {
                const searchTermLower = searchTerm.toLowerCase();
                poemsArray = poemsArray.filter(poem => 
                    Object.entries(poem).some(([key, value]) => 
                        typeof value === 'string' && 
                        value.toLowerCase().includes(searchTermLower)
                    )
                );
            }

            // Apply type filter if provided
            if (selectedType) {
                poemsArray = poemsArray.filter(poem => poem.type === selectedType);
            }

            // Calculate pagination
            const totalCount = poemsArray.length;
            const startIndex = (page - 1) * pageSize;
            const paginatedPoems = poemsArray.slice(startIndex, startIndex + pageSize);

            return { poems: paginatedPoems, totalCount };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// NEW: Async thunk to fetch only bookmarked poems for a specific user
export const fetchBookmarkedPoems = createAsyncThunk(
    'poems/fetchBookmarkedPoems',
    async ({ userId, page, pageSize, searchTerm }, { rejectWithValue }) => {
        try {
            const poemsRef = ref(db, 'poems');
            const snapshot = await get(poemsRef);
            const poems = snapshot.val() || {};
            
            // Filter poems that are bookmarked by the user
            let bookmarkedPoems = Object.keys(poems)
                .map(key => formatPoemData(poems[key], key))
                .filter(poem => poem.bookmarkedData && poem.bookmarkedData[userId]);

            // Sort by bookmark timestamp (most recently bookmarked first)
            bookmarkedPoems.sort((a, b) => {
                const aTimestamp = a.bookmarkedData[userId]?.timestamp || 0;
                const bTimestamp = b.bookmarkedData[userId]?.timestamp || 0;
                return bTimestamp - aTimestamp;
            });

            // Apply search filter if provided
            if (searchTerm) {
                const searchTermLower = searchTerm.toLowerCase();
                bookmarkedPoems = bookmarkedPoems.filter(poem =>
                    Object.entries(poem).some(([key, value]) =>
                        typeof value === 'string' && value.toLowerCase().includes(searchTermLower)
                    )
                );
            }

            const totalCount = bookmarkedPoems.length;
            const startIndex = (page - 1) * pageSize;
            const paginatedBookmarks = bookmarkedPoems.slice(startIndex, startIndex + pageSize);

            return { 
                bookmarkedPoems: paginatedBookmarks, 
                totalBookmarkedCount: totalCount 
            };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Async thunk for adding a bookmark to a poem
export const addPoemBookmark = createAsyncThunk(
    'poems/addBookmark',
    async ({ poemId, userName, poem }, { getState, rejectWithValue }) => {
        try {
            const userId = getState().auth.user?.uid;
            if (!userId) throw new Error('User must be authenticated to bookmark');

            const timestamp = Date.now();
            const bookmarkData = { userId, timestamp, poemId };

            // Update both user's bookmarks and poem's bookmarked by lists
            await Promise.all([
                set(ref(db, `users/${userId}/bookmarkedPoems/${poemId}`), bookmarkData),
                set(ref(db, `poems/${poemId}/bookmarkedBy/${userId}`), bookmarkData)
            ]);
            return { userId, poemId, bookmarkData };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Async thunk for removing a bookmark from a poem
export const removePoemBookmark = createAsyncThunk(
    'poems/removeBookmark',
    async ({ poemId, userName, poem }, { getState, rejectWithValue }) => {
        try {
            const userId = getState().auth.user?.uid;
            if (!userId) throw new Error('User must be authenticated to remove bookmark');

            // Remove bookmark from both user and poem records
            await Promise.all([
                remove(ref(db, `users/${userId}/bookmarkedPoems/${poemId}`)),
                remove(ref(db, `poems/${poemId}/bookmarkedBy/${userId}`))
            ]);
            return { userId, poemId };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Async thunk for adding a like to a poem
export const addLike = createAsyncThunk(
    'poems/addLike',
    async ({ poemId, userId, userName, poem }, { rejectWithValue }) => {
        try {
            const likeData = {
                userId,
                userName,
                timestamp: Date.now()
            };

            await set(ref(db, `poems/${poemId}/likes/${userId}`), likeData);
            return { poemId, userId, likeData };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Async thunk for removing a like from a poem
export const removeLike = createAsyncThunk(
    'poems/removeLike',
    async ({ poemId, userId, userName, poem }, { rejectWithValue }) => {
        try {
            await remove(ref(db, `poems/${poemId}/likes/${userId}`));
            return { poemId, userId };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Async thunk for adding a comment to a poem
export const addComment = createAsyncThunk(
    'poems/addComment',
    async ({ poemId, userName, comment, poem, userId }) => {
        const commentData = {
            userName,
            userId,
            text: comment,
            timestamp: Date.now(),
        };

        const commentsRef = ref(db, `poems/${poemId}/comments`);
        const newCommentRef = push(commentsRef);
        await set(newCommentRef, commentData);
        return { poemId, commentId: newCommentRef.key, ...commentData };
    }
);

// Selects the 3 most recently created poems
export const selectLatestPoems = createSelector(
    [selectAllPoems],
    poems => [...poems].sort((a, b) => b.createdAt - a.createdAt).slice(0, 3)
);

// Selects latest poems for current month
export const selectLatestPoemsThisMonth = createSelector(
  [selectAllPoems],
  (poems) => {
    const now = new Date();
    const currentMonth = now.getMonth(); // 0-indexed (0 = January)
    const currentYear = now.getFullYear();

    // Filter poems created in the current month & year
    const poemsThisMonth = poems.filter((poem) => {
      const createdAt = new Date(poem.createdAt);
      return (
        createdAt.getMonth() === currentMonth &&
        createdAt.getFullYear() === currentYear
      );
    });

    // Sort and get top 3 latest
    return poemsThisMonth
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 3);
  }
);

// Selects the 3 poems with the most likes 
export const selectMostLikedPoems = createSelector(
    [selectAllPoems],
    poems => [...poems]
        .sort((a, b) => (Object.keys(b.likes || {}).length - Object.keys(a.likes || {}).length))
        .slice(0, 3)
);

// Creates a map of users to their comment counts
export const selectCommentCountsByUser = createSelector(
    [selectAllPoems],
    (poems) => {
        const userCommentCounts = {};
        poems.forEach(poem => {
            if (poem.comments) {
                Object.values(poem.comments).forEach(comment => {
                    const { userId, userName } = comment;
                    if (!userId) return; // Ensure userId exists
                    if (!userCommentCounts[userId]) {
                        userCommentCounts[userId] = { userId, userName, count: 0 };
                    }
                    userCommentCounts[userId].count += 1;
                });
            }
        });
        return Object.values(userCommentCounts); // Return as an array for easier handling
    }
);

// Selects the user with the most comments
export const selectMostCommentedUser = createSelector(
    [selectCommentCountsByUser],
    (commentCounts) => {
        if (!commentCounts || commentCounts.length === 0) return null;
        return commentCounts.reduce((most, current) =>
            (!most || current.count > most.count) ? current : most, null
        );
    }
);

// Selects the top N users with the most comments
export const selectTopCommenters = createSelector(
    [selectCommentCountsByUser],
    (commentCounts, count = 3) =>
        [...commentCounts]
            .sort((a, b) => b.count - a.count)
            .slice(0, count)
            .map(({ userId, userName, count }) => ({
                userId,
                userName,
                commentCount: count
            }))
);

// Poem Slice Definition
const poemSlice = createSlice({
    name: 'poems',
    initialState: {
        poems: [],
        totalCount: 0,
        bookmarkedPoems: [], // NEW: Store bookmarked poems separately
        totalBookmarkedCount: 0, // NEW: Count of bookmarked poems
        loading: false,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Fetch Poems
            .addCase(fetchPoems.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPoems.fulfilled, (state, action) => {
                state.loading = false;
                state.poems = action.payload.poems;
                state.totalCount = action.payload.totalCount;
            })
            .addCase(fetchPoems.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // NEW: Fetch bookmarked poems
            .addCase(fetchBookmarkedPoems.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBookmarkedPoems.fulfilled, (state, action) => {
                state.loading = false;
                state.bookmarkedPoems = action.payload.bookmarkedPoems;
                state.totalBookmarkedCount = action.payload.totalBookmarkedCount;
            })
            .addCase(fetchBookmarkedPoems.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Add Bookmark
            .addCase(addPoemBookmark.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addPoemBookmark.fulfilled, (state, action) => {
                state.loading = false;
                const poem = state.poems.find(p => p.id === action.payload.poemId);
                if (poem) {
                    poem.bookmarkedData[action.payload.userId] = action.payload.bookmarkData;
                }
                // Also update in bookmarked poems if it exists there
                const bookmarkedPoem = state.bookmarkedPoems.find(p => p.id === action.payload.poemId);
                if (bookmarkedPoem) {
                    bookmarkedPoem.bookmarkedData[action.payload.userId] = action.payload.bookmarkData;
                }
            })
            .addCase(addPoemBookmark.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Remove Bookmark
            .addCase(removePoemBookmark.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(removePoemBookmark.fulfilled, (state, action) => {
                state.loading = false;
                const poem = state.poems.find(p => p.id === action.payload.poemId);
                if (poem) {
                    delete poem.bookmarkedData[action.payload.userId];
                }
                // Remove from bookmarked poems array
                state.bookmarkedPoems = state.bookmarkedPoems.filter(
                    p => p.id !== action.payload.poemId
                );
                state.totalBookmarkedCount = Math.max(0, state.totalBookmarkedCount - 1);
            })
            .addCase(removePoemBookmark.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Add Like
            .addCase(addLike.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addLike.fulfilled, (state, action) => {
                state.loading = false;
                const poem = state.poems.find(p => p.id === action.payload.poemId);
                if (poem) {
                    poem.likes[action.payload.userId] = action.payload.likeData;
                }
                // Also update in bookmarked poems if it exists there
                const bookmarkedPoem = state.bookmarkedPoems.find(p => p.id === action.payload.poemId);
                if (bookmarkedPoem) {
                    bookmarkedPoem.likes[action.payload.userId] = action.payload.likeData;
                }
            })
            .addCase(addLike.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Remove Like
            .addCase(removeLike.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(removeLike.fulfilled, (state, action) => {
                state.loading = false;
                const poem = state.poems.find(p => p.id === action.payload.poemId);
                if (poem) {
                    delete poem.likes[action.payload.userId];
                }
                // Also update in bookmarked poems if it exists there
                const bookmarkedPoem = state.bookmarkedPoems.find(p => p.id === action.payload.poemId);
                if (bookmarkedPoem) {
                    delete bookmarkedPoem.likes[action.payload.userId];
                }
            })
            .addCase(removeLike.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Add Comment
            .addCase(addComment.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addComment.fulfilled, (state, action) => {
                state.loading = false;
                const poem = state.poems.find(p => p.id === action.payload.poemId);
                if (poem) {
                    poem.comments[action.payload.commentId] = action.payload;
                }
                // Also update in bookmarked poems if it exists there
                const bookmarkedPoem = state.bookmarkedPoems.find(p => p.id === action.payload.poemId);
                if (bookmarkedPoem) {
                    bookmarkedPoem.comments[action.payload.commentId] = action.payload;
                }
            })
            .addCase(addComment.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export default poemSlice.reducer;